/**
 * Server functions for leads from a coach's Wheel of Life page: the public
 * start (rate limited, sets the httpOnly attempt cookie), the visitor's first
 * press of the results page's contact button, the coach's Leads tab, lead
 * page, stats, status, CSV export and delete, and the admin withdrawal. Thin:
 * validate, authorize, delegate to `~/lib/server/wheel/coach-leads`.
 *
 * Aliases: coach leads server functions, start coach wheel lead, export
 * leads csv, delete lead, withdraw leads, set lead status, contact click
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { authMiddleware } from "~/lib/auth-middleware";
import { permissionMiddleware } from "~/lib/permission-middleware";
import {
  CoachLeadStatusInputSchema,
  CoachWheelLeadStartSchema,
} from "~/lib/shared/coach-leads";
import { UserFacingError } from "~/lib/shared/user-facing-error";

import { requireWheelCoach } from "./coach-wheel";

const leadIdInput = z.object({ leadId: z.string().uuid() });

export const startCoachWheelLeadFn = createServerFn({ method: "POST" })
  .validator(CoachWheelLeadStartSchema)
  .handler(async ({ data }): Promise<void> => {
    const { startCoachWheelLead, COACH_LEAD_COOKIE_MAX_AGE_SECONDS } =
      await import("~/lib/server/wheel/coach-leads");
    const { getCoachWheelViewer } =
      await import("~/lib/server/wheel/coach-assessment");
    const { accessToken } = await startCoachWheelLead(
      data,
      await getCoachWheelViewer(),
    );
    const { getRequestUrl, setCookie } =
      await import("@tanstack/react-start/server");
    const { COOKIE_NAMES } = await import("~/lib/cookies");
    setCookie(COOKIE_NAMES.coachWheelLead, accessToken, {
      path: "/",
      httpOnly: true,
      sameSite: "lax",
      secure:
        process.env.NODE_ENV === "production" ||
        getRequestUrl().protocol === "https:",
      maxAge: COACH_LEAD_COOKIE_MAX_AGE_SECONDS,
    });
  });

/**
 * The visitor pressed the contact button on their results. Only the browser
 * holding the attempt's cookie can record it, and only for the Wheel that
 * attempt finished; anyone else is ignored.
 */
export const recordCoachWheelContactClickFn = createServerFn({ method: "POST" })
  .validator(z.object({ sessionId: z.string().min(1).max(20) }))
  .handler(async ({ data }): Promise<void> => {
    const { getCookie } = await import("@tanstack/react-start/server");
    const { COOKIE_NAMES } = await import("~/lib/cookies");
    const accessToken = getCookie(COOKIE_NAMES.coachWheelLead);
    if (!accessToken) return;
    const { recordCoachLeadContactClick } =
      await import("~/lib/server/wheel/coach-leads");
    await recordCoachLeadContactClick({
      accessToken,
      sessionId: data.sessionId,
    });
  });

export const listMyCoachLeadsFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const coachId = await requireWheelCoach(context.user);
    const { listCoachLeads } = await import("~/lib/server/wheel/coach-leads");
    return listCoachLeads(coachId);
  });

/** The Wheel card's last-30-days figures and sources. */
export const getMyCoachLeadStatsFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const coachId = await requireWheelCoach(context.user);
    const { getCoachLeadStats } =
      await import("~/lib/server/wheel/coach-lead-stats");
    return getCoachLeadStats(coachId);
  });

export const getMyCoachLeadFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(leadIdInput)
  .handler(async ({ data, context }) => {
    const coachId = await requireWheelCoach(context.user);
    const { getCoachLead } = await import("~/lib/server/wheel/coach-leads");
    return getCoachLead(coachId, data.leadId);
  });

export const setMyCoachLeadStatusFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(CoachLeadStatusInputSchema)
  .handler(async ({ data, context }) => {
    const coachId = await requireWheelCoach(context.user);
    const { setCoachLeadStatus } =
      await import("~/lib/server/wheel/coach-leads");
    const changed = await setCoachLeadStatus(coachId, data.leadId, data.status);
    if (!changed) throw new UserFacingError("This lead no longer exists.");
    return changed;
  });

export const deleteMyCoachLeadFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(leadIdInput)
  .handler(async ({ data, context }): Promise<{ deleted: boolean }> => {
    const coachId = await requireWheelCoach(context.user);
    const { deleteCoachLead } = await import("~/lib/server/wheel/coach-leads");
    return { deleted: await deleteCoachLead(coachId, data.leadId) };
  });

/** The coach's leads as CSV text; the browser saves it as a file. */
export const exportMyCoachLeadsCsvFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .handler(async ({ context }): Promise<string> => {
    const coachId = await requireWheelCoach(context.user);
    const { exportCoachLeadsCsv } =
      await import("~/lib/server/wheel/coach-leads");
    return exportCoachLeadsCsv(coachId);
  });

/** Support withdrawal: every lead for this email, across coaches. */
export const withdrawAssessmentLeadsFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("users:write")])
  .validator(z.object({ email: z.string().trim().toLowerCase().email() }))
  .handler(async ({ data }) => {
    const { withdrawAssessmentLeadsForEmail } =
      await import("~/lib/server/wheel/coach-leads");
    return withdrawAssessmentLeadsForEmail(data.email);
  });
